'use memo'

import React from 'react'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { PlayerDetailsFieldDef } from '@sportninja/common/features/profile/player/playerDetails'
import {
  DateOfBirthField,
  FieldRow,
  NumberField,
  PhoneField,
  SelectField,
  SwitchField,
  TextAreaField,
  TextField,
} from 'src/features/profile/player/setupModal/components/FormikField'
import DetailReadField from './DetailReadField'

interface DetailEditFieldProps {
  def: PlayerDetailsFieldDef
}

/**
 * The Edit Player form's own Formik field for a definition, so every state —
 * focus, filled, API error, disabled — matches that form.
 */
const DetailEditField: React.FC<DetailEditFieldProps> = ({ def }) => {
  if (def.locked) {
    return <DetailReadField def={def} />
  }

  const label = capitalizeWords(def.label)

  switch (def.kind) {
    case 'number':
      return (
        <NumberField
          name={def.name}
          label={label}
          addonAfter={def.unit}
          placeholder='0'
        />
      )
    case 'select':
      return (
        <SelectField
          name={def.name}
          label={label}
          options={[...(def.options ?? [])]}
          placeholder={capitalizeWords('Select...')}
        />
      )
    case 'textarea':
      return <TextAreaField name={def.name} label={label} placeholder={label} />
    case 'switch':
      return <SwitchField name={def.name} label={label} />
    case 'date':
      return <DateOfBirthField name={def.name} label={label} />
    case 'phone':
      return <PhoneField name={def.name} label={label} />
    case 'height':
      return (
        <FieldRow cols={2}>
          <NumberField
            name='heightFt'
            label={capitalizeWords('Height (ft)')}
            addonAfter='ft'
            placeholder='0'
          />
          <NumberField
            name='heightIn'
            label={capitalizeWords('Height (in)')}
            addonAfter='in'
            placeholder='0'
          />
        </FieldRow>
      )
    case 'text':
    default:
      return <TextField name={def.name} label={label} placeholder={label} />
  }
}

export default DetailEditField
